import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Alert,
    CircularProgress,
    Stack,
    Chip,
} from "@mui/material";
import { Add, Delete, Edit, Save, Refresh, Cancel } from "@mui/icons-material";

/**
 * Inspection Templates & Questions Builder (final)
 * HARD REQUIREMENTS ENFORCED:
 *  - InspectionMst: apiid (GUID), InspectionTitle (req), isActive (req)
 *  - InspectionQuestions: apiid (GUID), inspection_apiid (req), Text (req), Type (req)
 * API base: https://figazuresql.swancreate.com/api/coo
 * IMPORTANT: Questions are ALWAYS client-filtered by inspection_apiid
 *            even if the server returns all rows.
 */

const API_BASE = "https://figazuresql.swancreate.com/api/swa";

// UUID v4 (fallback polyfill)
function newUuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// JSON fetch (server expects application/json)
async function apiFetch(path, { method = "GET", body } = {}) {
    const headers = { Accept: "application/json" };
    if (body) headers["Content-Type"] = "application/json";

    const res = await fetch(`${API_BASE}/${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
    });

    const ct = res.headers.get("content-type") || "";
    const isJson = ct.includes("application/json");

    if (!res.ok) {
        const errText = isJson
            ? JSON.stringify(await res.json()).slice(0, 500)
            : (await res.text()).slice(0, 500);
        throw new Error(`${res.status} ${res.statusText} � ${errText}`);
    }
    return isJson ? res.json() : res.text();
}

const QUESTION_TYPES = ["YesNo", "Text", "Number", "MultipleChoice"];

export default function InspectionTemplatesPage() {
    const [inspections, setInspections] = useState([]);
    const [loadingInspections, setLoadingInspections] = useState(false);
    const [selected, setSelected] = useState(null); // selected InspectionMst row
    const [questions, setQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    // Dialog state for add/edit Inspection
    const [openDlg, setOpenDlg] = useState(false);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({
        apiid: "",
        InspectionTitle: "",
        Type: "",
        Description: "",
        CreatedOn: "",
        isActive: true,
    });

    // New question inline state
    const [newQ, setNewQ] = useState({ Text: "", Type: "YesNo" });

    const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
    const handleToast = (msg, sev = "success") => setToast({ open: true, msg, sev });

    const zebra = {
        "& tbody tr:nth-of-type(odd)": {
            backgroundColor: (theme) => theme.palette.action.hover,
        },
    };

    // Load Inspections
    const loadInspections = async () => {
        try {
            setLoadingInspections(true);
            const data = await apiFetch("InspectionMst", { method: "GET" });
            const sorted = Array.isArray(data)
                ? [...data].sort(
                    (a, b) => new Date(b.CreatedOn || 0) - new Date(a.CreatedOn || 0)
                )
                : [];
            setInspections(sorted);
        } catch (err) {
            handleToast(`Failed to load inspections: ${err.message}`, "error");
        } finally {
            setLoadingInspections(false);
        }
    };

    // Load Questions for selected inspection
    // ALWAYS client-filter by inspection_apiid to guarantee correct scoping
    const loadQuestions = async (inspectionApiId) => {
        if (!inspectionApiId) return setQuestions([]);
        try {
            setLoadingQuestions(true);

            // Try server-side filter first (some backends ignore the param)
            let server = await apiFetch(
                `InspectionQuestions?inspection_apiid=${encodeURIComponent(inspectionApiId)}`
            );

            // Normalize to array
            const arr = Array.isArray(server) ? server : [];
            // Force client-side filter regardless of server behavior
            const filtered = arr.filter((q) => q.inspection_apiid === inspectionApiId);

            // If server ignored filter and returned everything, filtered will fix it.
            setQuestions(filtered);
        } catch {
            // Fallback: GET all then filter
            try {
                const all = await apiFetch("InspectionQuestions", { method: "GET" });
                const filtered = Array.isArray(all)
                    ? all.filter((q) => q.inspection_apiid === inspectionApiId)
                    : [];
                setQuestions(filtered);
            } catch (err2) {
                handleToast(`Failed to load questions: ${err2.message}`, "error");
            }
        } finally {
            setLoadingQuestions(false);
        }
    };

    useEffect(() => {
        loadInspections();
    }, []);

    useEffect(() => {
        setQuestions([]); // clear stale list instantly on change
        if (selected?.apiid) loadQuestions(selected.apiid);
    }, [selected?.apiid]);

    const resetForm = () =>
        setForm({
            apiid: "",
            InspectionTitle: "",
            Type: "",
            Description: "",
            CreatedOn: "",
            isActive: true,
        });

    const openNewDialog = () => {
        resetForm();
        setEditing(false);
        setOpenDlg(true);
    };

    const openEditDialog = (row) => {
        setForm({
            apiid: row.apiid,
            InspectionTitle: row.InspectionTitle || "",
            Type: row.Type || "",
            Description: row.Description || "",
            CreatedOn: row.CreatedOn || "",
            isActive: Boolean(row.isActive),
        });
        setEditing(true);
        setOpenDlg(true);
    };

    // Save Inspection � enforce required fields
    const saveInspection = async () => {
        const title = (form.InspectionTitle || "").trim();
        if (!title) return handleToast("Inspection Title is required", "warning");

        const payload = {
            apiid: form.apiid || newUuid(), // REQUIRED
            InspectionTitle: title, // REQUIRED
            ...(form.Type?.trim() ? { Type: form.Type.trim() } : {}),
            ...(form.Description?.trim() ? { Description: form.Description.trim() } : {}),
            CreatedOn: form.CreatedOn || new Date().toISOString(),
            isActive: Boolean(form.isActive), // REQUIRED (bit)
        };

        try {
            if (editing) {
                await apiFetch("InspectionMst", { method: "PUT", body: payload });
                handleToast("Inspection updated.");
            } else {
                await apiFetch("InspectionMst", { method: "POST", body: payload });
                handleToast("Inspection created.");
            }
            setOpenDlg(false);
            await loadInspections();
            setSelected(payload);
        } catch (err) {
            handleToast(`Save failed: ${err.message}`, "error");
        }
    };

    const deleteInspection = async (row) => {
        if (
            !window.confirm(
                `Delete inspection �${row.InspectionTitle}�? This cannot be undone.`
            )
        )
            return;
        try {
            await apiFetch(
                `InspectionMst?apiid=${encodeURIComponent(row.apiid)}`,
                { method: "DELETE" }
            );
            handleToast("Inspection deleted.");
            if (selected?.apiid === row.apiid) setSelected(null);
            await loadInspections();
        } catch (err) {
            handleToast(`Delete failed: ${err.message}`, "error");
        }
    };

    // Add Question � enforce required fields
    const addQuestion = async () => {
        if (!selected?.apiid)
            return handleToast("Select or create an inspection first.", "warning");

        const text = (newQ.Text || "").trim();
        const qType = (newQ.Type || "").trim();

        if (!text) return handleToast("Question text is required.", "warning");
        if (!qType) return handleToast("Question type is required.", "warning");

        const payload = {
            apiid: newUuid(),
            inspection_apiid: selected.apiid,
            Text: text,
            Type: qType,
        };

        try {
            await apiFetch("InspectionQuestions", { method: "POST", body: payload });
            handleToast("Question added.");
            setNewQ({ Text: "", Type: qType });
            await loadQuestions(selected.apiid);
        } catch (err) {
            handleToast(`Add failed: ${err.message}`, "error");
        }
    };

    // Update Question � lock association to selected template and enforce required fields
    const updateQuestion = async (q) => {
        const payload = { ...q };

        // Make sure the question stays tied to the currently-selected inspection
        if (selected?.apiid) payload.inspection_apiid = selected.apiid;

        payload.Text = (payload.Text || "").trim();
        payload.Type = (payload.Type || "").trim();
        if (!payload.Text) return handleToast("Question text is required.", "warning");
        if (!payload.Type) return handleToast("Question type is required.", "warning");

        try {
            await apiFetch("InspectionQuestions", { method: "PUT", body: payload });
            handleToast("Question updated.");
            await loadQuestions(selected.apiid);
        } catch (err) {
            handleToast(`Update failed: ${err.message}`, "error");
        }
    };

    const deleteQuestion = async (q) => {
        if (!window.confirm("Delete this question?")) return;
        try {
            await apiFetch(
                `InspectionQuestions?apiid=${encodeURIComponent(q.apiid)}`,
                { method: "DELETE" }
            );
            handleToast("Question deleted.");
            await loadQuestions(selected.apiid);
        } catch (err) {
            handleToast(`Delete failed: ${err.message}`, "error");
        }
    };

    // Inline row editor for questions
    function QuestionRow({ row, index }) {
        const [edit, setEdit] = useState(false);
        const [local, setLocal] = useState({ ...row });

        useEffect(() => setLocal({ ...row }), [row.apiid]);

        return (
            <TableRow hover>
                <TableCell sx={{ width: 48 }}>
                    <Chip size="small" label={index + 1} />
                </TableCell>
                <TableCell>
                    {edit ? (
                        <TextField
                            required
                            fullWidth
                            size="small"
                            value={local.Text || ""}
                            onChange={(e) => setLocal({ ...local, Text: e.target.value })}
                        />
                    ) : (
                        <Typography variant="body2">{row.Text}</Typography>
                    )}
                </TableCell>
                <TableCell sx={{ width: 220 }}>
                    {edit ? (
                        <FormControl size="small" fullWidth required>
                            <InputLabel id={`qt-${row.apiid}`}>Type</InputLabel>
                            <Select
                                labelId={`qt-${row.apiid}`}
                                label="Type"
                                value={local.Type || "Text"}
                                onChange={(e) => setLocal({ ...local, Type: e.target.value })}
                                required
                            >
                                {QUESTION_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Typography variant="body2">{row.Type}</Typography>
                    )}
                </TableCell>
                <TableCell align="right" sx={{ width: 160 }}>
                    {edit ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Save">
                                <IconButton
                                    color="primary"
                                    onClick={() => {
                                        const text = (local.Text || "").trim();
                                        const qType = (local.Type || "").trim();
                                        if (!text) return handleToast("Question text is required.", "warning");
                                        if (!qType) return handleToast("Question type is required.", "warning");
                                        setEdit(false);
                                        updateQuestion({ ...local, Text: text, Type: qType });
                                    }}
                                >
                                    <Save />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton
                                    onClick={() => {
                                        setLocal({ ...row });
                                        setEdit(false);
                                    }}
                                >
                                    <Cancel />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Edit">
                                <IconButton onClick={() => setEdit(true)}>
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => deleteQuestion(row)}>
                                    <Delete />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </TableCell>
            </TableRow>
        );
    }

    return (
        <Box p={2}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h5" fontWeight={700}>
                            Inspection Templates & Questions
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            <Button startIcon={<Refresh />} onClick={loadInspections}>
                                Refresh
                            </Button>
                            <Button variant="contained" startIcon={<Add />} onClick={openNewDialog}>
                                New Inspection
                            </Button>
                        </Stack>
                    </Stack>
                </Grid>

                {/* LEFT: Inspections list */}
                <Grid item xs={12} md={6}>
                    <Card className="shadow-lg rounded-2xl">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Inspections
                            </Typography>

                            {loadingInspections ? (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={20} />{" "}
                                    <Typography variant="body2">Loading�</Typography>
                                </Box>
                            ) : (
                                <TableContainer sx={zebra}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Title</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Created</TableCell>
                                                <TableCell>Active</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {inspections.map((row) => (
                                                <TableRow
                                                    key={row.apiid}
                                                    hover
                                                    selected={selected?.apiid === row.apiid}
                                                    onClick={() => setSelected(row)}
                                                    sx={{ cursor: "pointer" }}
                                                >
                                                    <TableCell>
                                                        <Typography fontWeight={600}>
                                                            {row.InspectionTitle}
                                                        </Typography>
                                                        {row.Description ? (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block"
                                                                noWrap
                                                            >
                                                                {row.Description}
                                                            </Typography>
                                                        ) : null}
                                                    </TableCell>
                                                    <TableCell>{row.Type || "�"}</TableCell>
                                                    <TableCell>
                                                        {row.CreatedOn
                                                            ? new Date(row.CreatedOn).toLocaleString()
                                                            : "�"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Switch checked={Boolean(row.isActive)} disabled />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    openEditDialog(row);
                                                                }}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                color="error"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteInspection(row);
                                                                }}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {inspections.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={5}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No inspections yet. Click �New Inspection�.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* RIGHT: Questions editor (strictly scoped to selected inspection) */}
                <Grid item xs={12} md={6}>
                    <Card className="shadow-lg rounded-2xl">
                        <CardContent>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6" gutterBottom>
                                    Questions {selected ? `for �${selected.InspectionTitle}�` : "(select an inspection)"}
                                </Typography>
                                {!!selected && <Chip label={`Inspection ID: ${selected.apiid}`} size="small" />}
                            </Stack>

                            {!!selected ? (
                                <>
                                    <Box mb={2}>
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                            <TextField
                                                required
                                                fullWidth
                                                label="New Question Text"
                                                placeholder="e.g., Are holding temps above 140�F?"
                                                value={newQ.Text}
                                                onChange={(e) =>
                                                    setNewQ((s) => ({ ...s, Text: e.target.value }))
                                                }
                                                size="small"
                                            />
                                            <FormControl size="small" sx={{ minWidth: 180 }} required>
                                                <InputLabel id="new-q-type">Type</InputLabel>
                                                <Select
                                                    labelId="new-q-type"
                                                    label="Type"
                                                    value={newQ.Type}
                                                    onChange={(e) =>
                                                        setNewQ((s) => ({ ...s, Type: e.target.value }))
                                                    }
                                                    required
                                                >
                                                    {QUESTION_TYPES.map((t) => (
                                                        <MenuItem key={t} value={t}>
                                                            {t}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="contained"
                                                startIcon={<Add />}
                                                onClick={addQuestion}
                                            >
                                                Add Question
                                            </Button>
                                        </Stack>
                                    </Box>

                                    {loadingQuestions ? (
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CircularProgress size={20} />{" "}
                                            <Typography variant="body2">Loading�</Typography>
                                        </Box>
                                    ) : (
                                        <TableContainer sx={zebra}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>#</TableCell>
                                                        <TableCell>Question Text</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {questions.map((q, i) => (
                                                        <QuestionRow key={q.apiid} row={q} index={i} />
                                                    ))}
                                                    {questions.length === 0 && (
                                                        <TableRow>
                                                            <TableCell colSpan={4}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No questions yet. Add one above.
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </>
                            ) : (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    Choose or create an inspection on the left to start adding questions.
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Add/Edit Inspection Dialog */}
            <Dialog open={openDlg} onClose={() => setOpenDlg(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? "Edit Inspection" : "New Inspection"}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                fullWidth
                                required
                                label="Inspection Title"
                                value={form.InspectionTitle}
                                onChange={(e) =>
                                    setForm({ ...form, InspectionTitle: e.target.value })
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Type"
                                placeholder="e.g., Kitchen, Delivery, Safety"
                                value={form.Type}
                                onChange={(e) => setForm({ ...form, Type: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) =>
                                            setForm({ ...form, isActive: e.target.checked })
                                        }
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Description"
                                value={form.Description}
                                onChange={(e) => setForm({ ...form, Description: e.target.value })}
                            />
                        </Grid>
                        {editing && (
                            <Grid item xs={12}>
                                <Alert severity="info">
                                    apiid: <b>{form.apiid}</b>
                                </Alert>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDlg(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={saveInspection}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert elevation={6} variant="filled" severity={toast.sev} sx={{ width: "100%" }}>
                    {toast.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
